/**
 * Admin leaks dashboard (/admin/leaks).
 *
 * Two top-level tabs: "Current" (received + in_progress) and "Archive"
 * (success + failure). Within "Current", leaks are grouped by status.
 * Top-right "New leak" button opens a modal with ReportLeakForm in admin mode.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, ArrowRight, WarningCircle } from "@phosphor-icons/react";
import { leaksAdminApi, isClosed } from "../../api/leaks";
import PortalPageHeader from "../../components/portal/PortalPageHeader";
import PortalEmptyState from "../../components/portal/PortalEmptyState";
import LeakStatusBadge from "../../components/leaks/LeakStatusBadge";
import ReportLeakForm from "../../components/leaks/ReportLeakForm";
import Modal from "../../components/ui-shared/Modal";

const STATUS_GROUPS_CURRENT = ["received", "in_progress"];
const STATUS_GROUPS_ARCHIVE = ["success", "failure"];

function LeakRow({ leak }) {
  const ownerLabel = leak.owner
    ? leak.owner.first_name || leak.owner.email
    : "—";
  return (
    <Link
      to={`/admin/leaks/${leak.id}`}
      data-testid={`admin-leak-row-${leak.id}`}
      className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4 hover:border-slate-900 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 truncate">
          {leak.title}
        </p>
        <p className="text-xs text-slate-500 truncate">
          {ownerLabel} · {leak.url}
        </p>
      </div>
      <LeakStatusBadge status={leak.status} />
      <ArrowRight
        size={16}
        weight="bold"
        className="text-slate-400 group-hover:text-slate-900 group-hover:translate-x-1 transition-all"
      />
    </Link>
  );
}

export default function AdminLeaks() {
  const { t } = useTranslation();
  const [leaks, setLeaks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("current");
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setLeaks(await leaksAdminApi.list());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => {
    const statuses = tab === "current" ? STATUS_GROUPS_CURRENT : STATUS_GROUPS_ARCHIVE;
    return statuses.map((s) => ({
      status: s,
      items: leaks.filter((l) => l.status === s),
    }));
  }, [leaks, tab]);

  const counts = useMemo(
    () => ({
      current: leaks.filter((l) => !isClosed(l.status)).length,
      archive: leaks.filter((l) => isClosed(l.status)).length,
    }),
    [leaks],
  );

  return (
    <div data-testid="admin-leaks-page" className="space-y-10">
      <PortalPageHeader
        kickerKey="admin.placeholder.kicker"
        titleKey="admin.nav.leaks"
        subtitleKey="admin.pages.leaks_desc"
        right={
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            data-testid="admin-leaks-new-button"
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            <Plus size={14} weight="bold" />
            {t("leaks.admin_new_button")}
          </button>
        }
      />

      <div
        data-testid="admin-leaks-tabs"
        className="flex items-center gap-1 border-b border-slate-200"
      >
        <button
          type="button"
          onClick={() => setTab("current")}
          data-testid="admin-leaks-tab-current"
          className={`relative px-4 py-3 text-sm font-medium transition-colors ${
            tab === "current"
              ? "text-slate-900"
              : "text-slate-500 hover:text-slate-900"
          }`}
        >
          {t("leaks.current")}{" "}
          <span className="text-slate-400 font-normal">({counts.current})</span>
          {tab === "current" && (
            <span className="absolute inset-x-3 -bottom-px h-0.5 bg-slate-900" />
          )}
        </button>
        <button
          type="button"
          onClick={() => setTab("archive")}
          data-testid="admin-leaks-tab-archive"
          className={`relative px-4 py-3 text-sm font-medium transition-colors ${
            tab === "archive"
              ? "text-slate-900"
              : "text-slate-500 hover:text-slate-900"
          }`}
        >
          {t("leaks.archive")}{" "}
          <span className="text-slate-400 font-normal">({counts.archive})</span>
          {tab === "archive" && (
            <span className="absolute inset-x-3 -bottom-px h-0.5 bg-slate-900" />
          )}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">{t("common.loading")}</p>
      ) : leaks.length === 0 ? (
        <PortalEmptyState
          variant="dashed"
          icon={WarningCircle}
          titleKey="leaks.admin_empty_title"
          descKey="leaks.admin_empty_desc"
        />
      ) : (
        <div className="space-y-8">
          {groups.map((g) => (
            <section key={g.status} data-testid={`admin-leaks-group-${g.status}`}>
              <div className="flex items-center gap-3 mb-3">
                <LeakStatusBadge status={g.status} />
                <span className="text-xs text-slate-500">
                  {g.items.length} {g.items.length > 1 ? t("leaks.entries_p") : t("leaks.entries_s")}
                </span>
              </div>
              {g.items.length === 0 ? (
                <p className="text-xs text-slate-400">{t("leaks.no_entry")}</p>
              ) : (
                <div className="space-y-2">
                  {g.items.map((l) => (
                    <LeakRow key={l.id} leak={l} />
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t("leaks.admin_new_modal_title")}
        testId="admin-leak-new-modal"
      >
        <ReportLeakForm
          mode="admin"
          onCancel={() => setModalOpen(false)}
          onCreated={() => {
            setModalOpen(false);
            load();
          }}
        />
      </Modal>
    </div>
  );
}
